import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { ProductService } from '../pages/service/product.service';
import { CustomerPortalUserService, CustomerPortalUserSummary } from '../services/customer-portal-user.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import {
    CustomerDisputeSummary,
    CustomerOrderHeader,
    StaffCustomerViewService
} from '../services/staff-customer-view.service';
import { openInvoiceView } from './customer-support-invoice.util';

interface M3CustomerOption {
    CUNO?: string;
    CUNM?: string;
    WHLO?: string;
    CUSTOMER?: string;
}

@Component({
    selector: 'app-customer-support-view',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        TableModule,
        ButtonModule,
        AutoCompleteModule,
        TabViewModule,
        TagModule,
        ToastModule,
        ToolbarModule
    ],
    providers: [MessageService],
    templateUrl: './customer-support-view.component.html',
    styleUrl: './customer-support-view.component.scss'
})
export class CustomerSupportViewComponent implements OnInit {
    customerSearchOptions: M3CustomerOption[] = [];
    selectedM3Customer: M3CustomerOption | null = null;
    searchQuery = '';

    selectedCustomer: CustomerPortalUserSummary | null = null;
    loadingLookup = false;
    loadingOrders = false;
    loadingDisputes = false;

    orders: CustomerOrderHeader[] = [];
    disputes: CustomerDisputeSummary[] = [];
    invoiceLoadingOrder: string | null = null;

    constructor(
        private customerPortalUserService: CustomerPortalUserService,
        private staffCustomerViewService: StaffCustomerViewService,
        private productService: ProductService,
        private inMemoryStorage: InMemoryStorageService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        const company = this.inMemoryStorage.get('company') || this.productService.getCompanyId();
        const division = this.inMemoryStorage.get('division') || this.productService.getDivisionId();
        if (company && division) {
            this.productService.setCompanyAndDivision(company, division);
        }

        const customerId = this.route.snapshot.queryParamMap.get('customerId');
        if (customerId) {
            this.searchQuery = customerId;
            this.lookupPortalCustomer(customerId);
        }
    }

    goBack(): void {
        this.router.navigate(['/dashboard']);
    }

    searchM3Customers(event: AutoCompleteCompleteEvent): void {
        const query = event.query?.trim() ?? '';
        this.searchQuery = query;
        if (query.length < 3) {
            this.customerSearchOptions = [];
            return;
        }

        const search$ = /^\d+$/.test(query)
            ? this.productService.searchCustomerNumbers(query)
            : this.productService.searchCustomers(query);

        search$.subscribe({
            next: (res) => {
                this.customerSearchOptions = Array.isArray(res) ? res : res ? [res] : [];
            },
            error: () => {
                this.customerSearchOptions = [];
            }
        });
    }

    onM3CustomerSelect(event: AutoCompleteSelectEvent): void {
        const customer = event.value as M3CustomerOption;
        const customerId = customer?.CUNO?.trim();
        if (!customerId) return;
        this.lookupPortalCustomer(customerId);
    }

    lookupPortalCustomer(customerId: string): void {
        this.loadingLookup = true;
        this.selectedCustomer = null;
        this.orders = [];
        this.disputes = [];

        this.customerPortalUserService
            .lookup(customerId)
            .pipe(finalize(() => (this.loadingLookup = false)))
            .subscribe({
                next: (customer) => {
                    this.selectedCustomer = customer;
                    this.loadOrders();
                    this.loadDisputes();
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'No portal account',
                        detail:
                            'No portal account for this customer — create one in Customer Portal Users first.'
                    });
                }
            });
    }

    loadOrders(): void {
        if (!this.selectedCustomer) return;
        this.loadingOrders = true;
        this.staffCustomerViewService
            .listOrders(this.selectedCustomer.customerId, { page: 0, pageSize: 50 })
            .pipe(finalize(() => (this.loadingOrders = false)))
            .subscribe({
                next: (page) => (this.orders = page.content ?? []),
                error: (err) => this.showError('Failed to load orders', err)
            });
    }

    loadDisputes(): void {
        if (!this.selectedCustomer) return;
        this.loadingDisputes = true;
        this.staffCustomerViewService
            .listDisputes(this.selectedCustomer.customerId)
            .pipe(finalize(() => (this.loadingDisputes = false)))
            .subscribe({
                next: (list) => (this.disputes = list ?? []),
                error: (err) => this.showError('Failed to load disputes', err)
            });
    }

    openOrderDetail(order: CustomerOrderHeader): void {
        if (!this.selectedCustomer) return;
        this.router.navigate(
            ['/customer-support', this.selectedCustomer.customerId, 'orders', order.orderNumber],
            {
                queryParams: {
                    company: this.selectedCustomer.company || order.companyId,
                    division: this.selectedCustomer.division || order.divisionId
                }
            }
        );
    }

    openDisputeDetail(dispute: CustomerDisputeSummary): void {
        if (!this.selectedCustomer) return;
        this.router.navigate(
            ['/customer-support', this.selectedCustomer.customerId, 'disputes', dispute.claimNumber],
            {
                queryParams: {
                    company: this.selectedCustomer.company || dispute.companyId,
                    division: this.selectedCustomer.division || dispute.divisionId
                }
            }
        );
    }

    viewInvoice(order: CustomerOrderHeader, event?: Event): void {
        event?.stopPropagation();
        if (!this.selectedCustomer) return;
        this.invoiceLoadingOrder = order.orderNumber;
        this.staffCustomerViewService
            .getInvoiceView(this.selectedCustomer.customerId, order.orderNumber)
            .pipe(finalize(() => (this.invoiceLoadingOrder = null)))
            .subscribe({
                next: (view) => {
                    const result = openInvoiceView(view);
                    if (result !== 'opened') {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Invoice',
                            detail:
                                result === 'empty'
                                    ? 'Invoice PDF was empty'
                                    : `No invoice available for ${order.invoiceNumber || order.orderNumber}`
                        });
                    }
                },
                error: (err) => this.showError('Invoice view failed', err)
            });
    }

    private showError(summary: string, err: unknown): void {
        const detail =
            typeof (err as { error?: unknown })?.error === 'string'
                ? ((err as { error: string }).error as string)
                : (err as { message?: string })?.message || 'Unexpected error';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
